import React, { useEffect, useState } from 'react'

import { KeyboardDatePicker } from '@material-ui/pickers'
import {
    Box,
    Button,
    Checkbox,
    Modal,
    Paper,
    Slide,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import EventIcon from '@material-ui/icons/Event'

import { CreateDeadlineRequest, useUser } from '../../../../hooks'
import { ApartmentDeadline, IdBoolMap } from '../../../../models'
import { FormState } from '../../types'
import { APARTMENT, dateValid } from '../../../../helpers'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'

interface Props {
    theme: Theme
    formState: FormState
    onCreate: (request: CreateDeadlineRequest) => Promise<ApartmentDeadline>
    onClose: () => void
    /** When true (default), pre-select the current workspace. Set to false e.g. on EZFolio Deadlines page. */
    autoSelectCurrentWorkspace?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    modalBackdrop: {
        display: 'flex',
    },
    paper: {
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
        borderRadius: 12,
        boxShadow: theme.shadows[10],
        maxHeight: '90vh',
        maxWidth: 560,
        width: '100%',
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    headerIcon: {
        color: theme.palette.primary.main,
        fontSize: 28,
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: '1.25rem',
    },
    body: {
        padding: theme.spacing(3),
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        '& > *': { flex: '1 1 200px', minWidth: 0 },
    },
    sectionLabel: {
        marginBottom: theme.spacing(0.5),
        fontWeight: 600,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    propertiesSection: {
        marginTop: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
    },
    propertiesHeader: {
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.action.hover,
    },
    propertiesList: {
        maxHeight: 220,
        overflowY: 'auto',
    },
    propertyRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.25, 2),
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    footer: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    cancelButton: {
        textTransform: 'none',
    },
    createButton: {
        textTransform: 'none',
        minWidth: 120,
    },
}))

export const AddDeadlineModal = (props: Props) => {
    const {
        formState,
        theme,
        onClose,
        autoSelectCurrentWorkspace = true,
    } = props
    const classes = useStyles()

    const [doValidate, setDoValidate] = useState(false)

    const [date, setDate] = useState(formState.meta.date)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [reminderDate, setReminderDate] = useState<DateTime | null>(null)

    const { rootUser, workspaceUser } = useUser()

    const [
        selectedPropertyState,
        setSelectedPropertyState,
    ] = useState<IdBoolMap>({})

    useEffect(() => {
        setDate(formState.meta.date)
        setTitle('')
        setDescription('')
        setReminderDate(null)
        setDoValidate(false)
        if (autoSelectCurrentWorkspace) {
            setCurrentWorkspaceSelection()
        } else {
            setSelectedPropertyState({})
        }
    }, [formState, rootUser, workspaceUser, autoSelectCurrentWorkspace])

    const setCurrentWorkspaceSelection = () => {
        if (workspaceUser) {
            setSelectedPropertyState({
                [workspaceUser.active_workspace.id]: true,
            })
        }
    }

    const getIsValid = () => {
        const titleValid = title !== ''

        const dv = dateValid(date.toJSDate())

        return titleValid && dv && selectedCount > 0
    }

    const selectedIds = Object.keys(selectedPropertyState).reduce<number[]>(
        (prev, strKey) => {
            const key = Number(strKey)
            if (selectedPropertyState[key]) {
                return prev.concat(key)
            }
            return prev
        },
        [],
    )

    const selectedCount = selectedIds.length

    const open = formState.modals.addDeadlineModal

    const filteredWorkspaces =
        rootUser?.workspaces.filter((wsOption) => {
            return (
                wsOption.active_workspace.organization ===
                    workspaceUser?.active_workspace.organization &&
                wsOption.active_workspace.company_type === APARTMENT
            )
        }) ?? []

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modalBackdrop}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    {/* Header */}
                    <Box className={classes.header}>
                        <EventIcon className={classes.headerIcon} />
                        <Typography
                            className={classes.headerTitle}
                            color="textPrimary"
                        >
                            Add a new deadline
                        </Typography>
                    </Box>

                    {/* Body */}
                    <Box className={classes.body}>
                        <Box className={classes.formRow}>
                            <KeyboardDatePicker
                                format="MM/dd/yyyy"
                                variant="inline"
                                inputVariant="outlined"
                                value={date}
                                error={
                                    doValidate && !dateValid(date.toJSDate())
                                }
                                label="Due date"
                                onChange={(newDate) => {
                                    if (newDate) {
                                        setDate(DateTime.fromJSDate(newDate))
                                    }
                                }}
                                required
                                fullWidth
                            />
                            <TextField
                                fullWidth
                                variant="outlined"
                                error={doValidate && title === ''}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                label="Title"
                                placeholder="e.g. Lease renewal, Inspection"
                                required
                            />
                        </Box>

                        <Box>
                            <Typography className={classes.sectionLabel}>
                                Description (optional)
                            </Typography>
                            <TextField
                                fullWidth
                                value={description}
                                variant="outlined"
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Add any notes or details..."
                                multiline
                                rows={3}
                                size="small"
                            />
                        </Box>

                        <Box>
                            <Typography className={classes.sectionLabel}>
                                Reminder Date (optional)
                            </Typography>
                            <KeyboardDatePicker
                                format="MM/dd/yyyy"
                                variant="inline"
                                inputVariant="outlined"
                                value={reminderDate}
                                label="Reminder date"
                                onChange={(newDate) => {
                                    if (newDate) {
                                        setReminderDate(
                                            DateTime.fromJSDate(newDate),
                                        )
                                    } else {
                                        setReminderDate(null)
                                    }
                                }}
                                clearable
                                fullWidth
                                placeholder="Select a reminder date"
                                helperText="You'll receive an email with apartment progress on this date"
                            />
                        </Box>

                        <Box>
                            <Typography className={classes.sectionLabel}>
                                Apply to properties
                            </Typography>
                            <Box className={classes.propertiesSection}>
                                <Box className={classes.propertiesHeader}>
                                    <Checkbox
                                        checked={
                                            selectedCount ===
                                            filteredWorkspaces.length
                                        }
                                        indeterminate={
                                            selectedCount > 0 &&
                                            selectedCount <
                                                filteredWorkspaces.length
                                        }
                                        onChange={() => {
                                            if (selectedCount > 0) {
                                                setSelectedPropertyState({})
                                            } else {
                                                const newState: IdBoolMap = {}
                                                filteredWorkspaces.forEach(
                                                    (ws) => {
                                                        newState[
                                                            ws.active_workspace.id
                                                        ] = true
                                                    },
                                                )
                                                setSelectedPropertyState(
                                                    newState,
                                                )
                                            }
                                        }}
                                        color="primary"
                                    />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {selectedCount === 0
                                            ? 'Select at least one property'
                                            : `${selectedCount} selected`}
                                    </Typography>
                                    <Box flex={1} />
                                    {!autoSelectCurrentWorkspace && (
                                        <Tooltip title="Select only current workspace">
                                            <Button
                                                size="small"
                                                onClick={
                                                    setCurrentWorkspaceSelection
                                                }
                                                style={{
                                                    textTransform: 'none',
                                                }}
                                            >
                                                Current only
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box className={classes.propertiesList}>
                                    {filteredWorkspaces.map((ws) => {
                                        const propertyId =
                                            ws.active_workspace.id
                                        const isChecked =
                                            selectedPropertyState[
                                                propertyId
                                            ] === true
                                        return (
                                            <Box
                                                key={`WS-${propertyId}`}
                                                className={classes.propertyRow}
                                                onClick={() => {
                                                    setSelectedPropertyState({
                                                        ...selectedPropertyState,
                                                        [propertyId]: !isChecked,
                                                    })
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isChecked}
                                                    color="primary"
                                                    size="small"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onChange={() => {
                                                        setSelectedPropertyState(
                                                            {
                                                                ...selectedPropertyState,
                                                                [propertyId]: !isChecked,
                                                            },
                                                        )
                                                    }}
                                                />
                                                <Typography variant="body2">
                                                    {ws.active_workspace.name}
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box className={classes.footer}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            className={classes.cancelButton}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.createButton}
                            disabled={selectedCount === 0}
                            onClick={() => {
                                setDoValidate(true)
                                if (getIsValid()) {
                                    props
                                        .onCreate({
                                            name: title,
                                            description: description,
                                            date:
                                                date.toISO() ??
                                                new Date().toISOString(),
                                            apartments: selectedIds,
                                            reminder_date:
                                                reminderDate?.toISO() ?? null,
                                        })
                                        .then(onClose)
                                        .catch((e) => {
                                            toast.error(
                                                `Unable to add deadline: ${e}`,
                                            )
                                        })
                                }
                            }}
                        >
                            Create
                            {selectedCount > 0 && ` (${selectedCount})`}
                        </Button>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}
